import type {
  AppendCommissionAdjustmentRequest,
  AppendCommissionAdjustmentResponse,
  CommissionStatementStatus,
  ConfirmCommissionStatementResponse,
  GetCommissionStatementResponse,
  ListCommissionStatementsResponse,
  MarkCommissionPayoutRequest,
  MarkCommissionPayoutResponse,
} from '@nadoutong/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed client + React Query hooks for `/v1/ops/commission/statements/*`
 * — the commission settlement queue (epic #1034 E5-M4, #1146).
 *
 * Finance-manager + admin only (ops-api enforces the role gate). The
 * displayed total on every statement is the stored as-confirmed total
 * plus the sum of its adjustment rows; the client only ever renders those
 * server-computed figures. All queries share the `['commission-settlement']`
 * root key so a confirm / adjust / payout mutation invalidates the queue
 * and any open detail together.
 */

const BASE_PATH = '/v1/ops/commission/statements';

export const COMMISSION_SETTLEMENT_ROOT_KEY = ['commission-settlement'] as const;

/** 30s polling for the settlement queue, matching the receipts precedent. */
export const COMMISSION_SETTLEMENT_POLL_INTERVAL_MS = 30_000;

export interface CommissionStatementListScope {
  readonly limit: number;
  readonly offset: number;
  /** '' = all statuses. */
  readonly status: CommissionStatementStatus | '';
  /** '' = all brands. */
  readonly brand: string;
  /** '' = all periods; otherwise a 'YYYY-MM' key. */
  readonly period: string;
}

function listPath(scope: CommissionStatementListScope): string {
  const params = new URLSearchParams();
  params.set('limit', String(scope.limit));
  params.set('offset', String(scope.offset));
  if (scope.status !== '') {
    params.set('status', scope.status);
  }
  if (scope.brand.length > 0) {
    params.set('brand', scope.brand);
  }
  if (scope.period.length > 0) {
    params.set('period', scope.period);
  }
  return `${BASE_PATH}?${params.toString()}`;
}

export function commissionStatementListQueryKey(
  scope: CommissionStatementListScope,
): readonly ['commission-settlement', 'list', CommissionStatementListScope] {
  return ['commission-settlement', 'list', scope] as const;
}

export function commissionStatementDetailQueryKey(
  id: string,
): readonly ['commission-settlement', 'detail', string] {
  return ['commission-settlement', 'detail', id] as const;
}

export async function listCommissionStatements(
  scope: CommissionStatementListScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListCommissionStatementsResponse> {
  return opsApiRequest<ListCommissionStatementsResponse>(listPath(scope), { signal }, fetchImpl);
}

export function useCommissionStatements(
  scope: CommissionStatementListScope,
  poll = false,
): UseQueryResult<ListCommissionStatementsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: commissionStatementListQueryKey(scope),
    queryFn: ({ signal }) => listCommissionStatements(scope, fetchImpl, signal),
    ...(poll ? { refetchInterval: COMMISSION_SETTLEMENT_POLL_INTERVAL_MS } : {}),
  });
}

export async function getCommissionStatement(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetCommissionStatementResponse> {
  return opsApiRequest<GetCommissionStatementResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    { signal },
    fetchImpl,
  );
}

export function useCommissionStatement(
  id: string | null,
): UseQueryResult<GetCommissionStatementResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: commissionStatementDetailQueryKey(id ?? ''),
    queryFn: ({ signal }) => getCommissionStatement(id ?? '', fetchImpl, signal),
    enabled: id !== null,
  });
}

export function useConfirmCommissionStatement(): UseMutationResult<
  ConfirmCommissionStatementResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      opsApiRequest<ConfirmCommissionStatementResponse>(
        `${BASE_PATH}/${encodeURIComponent(id)}/confirm`,
        { method: 'POST', body: {} },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COMMISSION_SETTLEMENT_ROOT_KEY });
    },
  });
}

export interface AppendAdjustmentVariables {
  readonly id: string;
  readonly body: AppendCommissionAdjustmentRequest;
}

export function useAppendCommissionAdjustment(): UseMutationResult<
  AppendCommissionAdjustmentResponse,
  Error,
  AppendAdjustmentVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) =>
      opsApiRequest<AppendCommissionAdjustmentResponse>(
        `${BASE_PATH}/${encodeURIComponent(id)}/adjustments`,
        { method: 'POST', body },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COMMISSION_SETTLEMENT_ROOT_KEY });
    },
  });
}

export interface MarkPayoutVariables {
  readonly id: string;
  readonly body: MarkCommissionPayoutRequest;
}

export function useMarkCommissionPayout(): UseMutationResult<
  MarkCommissionPayoutResponse,
  Error,
  MarkPayoutVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) =>
      opsApiRequest<MarkCommissionPayoutResponse>(
        `${BASE_PATH}/${encodeURIComponent(id)}/payout`,
        { method: 'POST', body },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COMMISSION_SETTLEMENT_ROOT_KEY });
    },
  });
}
